import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getCompletedHomework } from "../helper/apicalls/homeworks";
import MCQ from "../components/MCQ";
import TrueFalse from "../components/TrueFalse";
import Match from "../components/Match";
import Fillups from "../components/Fillups";
import QuestionPalette from "../components/QuestionPalette";
import HomeworkFeedback from "../components/HomeworkFeedback";
import GlobalLoader from "../components/GlobalLoader";
import PsychologyIcon from "@mui/icons-material/Psychology";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import notAvailable from "../assets/images/notAvalaible.svg";
import "../styles/TestPage.scss";

const HomeworkReviewPage = () => {
  const [searchParams] = useSearchParams();
  const homeworkId = searchParams.get("homework_id");
  const homeworkLevel = searchParams.get("level");
  const title = searchParams.get("title");
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [testAnswers, setTestAnswers] = useState({});
  const [testScore, setTestScore] = useState(0);
  const [homeworkFeedback, setHomeworkFeedback] = useState([]);
  const [showHomeworkAnswers, setShowHomeworkAnswers] = useState(false);
  const [testMetadata, setTestMetadata] = useState(null);
  const [visitedQuestions, setVisitedQuestions] = useState(new Set([0]));
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    const fetchHomeworkDetails = async () => {
      try {
        setLoading(true);
        if (!homeworkId) {
          throw new Error("No homework ID provided");
        }

        const hwResponse = await getCompletedHomework(homeworkId, homeworkLevel);
        const hwData = hwResponse?.data || hwResponse;
        const hwResult = hwData?.data || hwData;

        if (
          hwResult?.attempt_data &&
          Array.isArray(hwResult.attempt_data)
        ) {
          const reconstructedQuestions = hwResult.attempt_data.map(
            (item, idx) => {
              let type = item.type || "mcq";

              if (
                type === "mcq" &&
                (!item.options || item.options.length === 0) &&
                (String(item.answer || "").toLowerCase() === "true" ||
                  String(item.answer || "").toLowerCase() === "false")
              ) {
                type = "truefalse";
              }

              if (Array.isArray(item.pairs) && item.pairs.length > 0) {
                type = "match";
              }

              const sequence = item.sequence || idx + 1;

              return {
                sequence,
                type,
                content: {
                  question_id: sequence,
                  question: item.question,
                  options: item.options,
                  pairs: item.pairs || [],
                  answer: item.answer,
                  explanation: item.explanation,
                  hint: item.hint,
                },
              };
            },
          );

          const answersMap = {};
          hwResult.attempt_data.forEach((item, idx) => {
            const sequence = item.sequence || idx + 1;
            let type = item.type || "mcq";

            if (Array.isArray(item.pairs) && item.pairs.length > 0) {
              type = "match";
            } else if (
              type === "mcq" &&
              (!item.options || item.options.length === 0) &&
              (String(item.answer || "").toLowerCase() === "true" ||
                String(item.answer || "").toLowerCase() === "false")
            ) {
              type = "truefalse";
            }

            let ans = item.user_answer;
            if (ans !== undefined && ans !== null) {
              if (type === "trueorfalse" || type === "truefalse") {
                if (String(ans).toLowerCase() === "true") ans = "A";
                else if (String(ans).toLowerCase() === "false") ans = "B";
                else if (String(ans).toLowerCase() === "a") ans = "A";
                else if (String(ans).toLowerCase() === "b") ans = "B";
              } else if (type === "mcq" || type === "quiz") {
                if (typeof ans === "string") {
                  ans = ans.toUpperCase();
                }
              }
              answersMap[sequence] = ans;
            }
          });

          setQuestions(reconstructedQuestions);
          setTestAnswers(answersMap);
          setTestScore(hwResult.no_of_question_correct || 0);
          setHomeworkFeedback(hwResult.feedback || []);
          setTestMetadata({
            id: hwResult.id || homeworkId,
            name: title || hwResult.name || hwResult.title || "Homework Review",
          });
        } else {
          throw new Error("Invalid homework attempt data");
        }
      } catch (err) {
        console.error("Failed to load homework details:", err);
        setError(err.message || "Failed to load homework results");
      } finally {
        setLoading(false);
      }
    };

    fetchHomeworkDetails();
  }, [homeworkId, homeworkLevel, title]);

  const getQuestionId = (q) => {
    let content = q.content || q;
    try {
      if (typeof content === "string") content = JSON.parse(content);
      if (typeof content === "string") content = JSON.parse(content);
    } catch (e) {}

    const type = (q.type || "").toLowerCase().trim();
    const questionObj =
      type === "match" ? content : content?.questions?.[0] || content;

    return (
      content?.question_id ||
      content?.id ||
      q.question_id ||
      q.id ||
      q._id ||
      q.sequence ||
      questionObj?.question_id ||
      questionObj?.id ||
      questionObj?.question
    );
  };

  const getQuestionHeading = (type) => {
    const map = {
      mcq: "Multiple Choice Questions",
      trueorfalse: "True or False",
      match: "Match the Following",
      fillups: "Fill in the Blanks",
    };

    return map[type?.toLowerCase()] || "Questions";
  };

  const getCategoryLabel = (category) => {
    if (!category) return "";

    const map = {
      application: "Application",
      reasoning: "Reasoning",
      direct: "Direct",
      critical_thinking: "Critical Thinking",
    };

    const key = category.toLowerCase();
    if (map[key]) return map[key];

    const withoutUnderscore = category.replace(/_/g, " ");
    return withoutUnderscore
      .toLowerCase()
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      application: "/assets/images/applicationImg.svg",
      direct: "/assets/images/directImg.svg",
      reasoning: "/assets/images/reasoningImg.svg",
      critical_thinking: "/assets/images/criticalThinkingImg.svg",
    };

    const iconSrc = iconMap[category?.toLowerCase()];
    if (iconSrc) {
      return <img src={iconSrc} alt={category} className="category-icon" />;
    } else {
      return (
        <img
          src={"/assets/images/defaultBrainIcon.svg"}
          alt={category}
          className="category-icon"
        />
      );
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => {
        const next = prev + 1;
        setVisitedQuestions((v) => {
          const newSet = new Set(v);
          newSet.add(next);
          return newSet;
        });
        return next;
      });
      setShowHint(false);
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => {
        const next = prev - 1;
        setVisitedQuestions((v) => {
          const newSet = new Set(v);
          newSet.add(next);
          return newSet;
        });
        return next;
      });
      setShowHint(false);
    }
  };

  const renderCurrentQuestion = () => {
    const questionData = questions[currentQuestionIndex];
    if (!questionData) return null;

    let content = questionData.content || questionData;
    try {
      if (typeof content === "string") {
        content = JSON.parse(content);
      }
      if (typeof content === "string") {
        content = JSON.parse(content);
      }
    } catch (e) {
      console.error("Error parsing content:", e);
    }

    if (content && Array.isArray(content.questions)) {
      const explanationItemIndex = content.questions.findIndex(
        (q) => q.explanation && !q.question && !q.key,
      );
      if (explanationItemIndex !== -1) {
        content.explanation =
          content.questions[explanationItemIndex].explanation;
        content.questions = content.questions.filter(
          (_, i) => i !== explanationItemIndex,
        );
      }
    }

    const type = (questionData.type || "").toLowerCase();
    const category = questionData.categories;
    const hint = (content && content.hint) || questionData.hint;

    if (questionData.sequence) {
      if (Array.isArray(content.questions)) {
        content.questions = content.questions.map((q) => ({
          ...q,
          question_id: q.question_id || questionData.sequence,
        }));
      }
    }

    const questionIdForComponent = getQuestionId(questionData);

    return (
      <div className="question-container">
        <div className="title-wrapper">
          {type && (
            <div className="main-title">
              <span className="title-icon">
                <PsychologyIcon />
              </span>
              {getQuestionHeading(type)}
            </div>
          )}

          {(category || hint) && (
            <div className="question-category">
              {hint && (
                <button
                  className="hint-button"
                  onClick={() => setShowHint(!showHint)}
                  title="Show Hint"
                >
                  <LightbulbIcon />
                  <strong className="desktop-item">Hint</strong>
                </button>
              )}
              {category && (
                <span
                  className={`category-badge ${category
                    .toLowerCase()
                    .replaceAll("_", "")}`}
                >
                  {getCategoryIcon(category)}
                  {getCategoryLabel(category)}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="question-content custom-scrollbar">
          {(() => {
            switch (type) {
              case "match":
                return (
                  <Match
                    key={questionIdForComponent}
                    data={content}
                    mode="review"
                    answers={testAnswers}
                    setAnswers={setTestAnswers}
                    questionId={questionIdForComponent}
                    retest={false}
                    isAttended={questionData?.attended || content?.attended}
                  />
                );
              case "trueorfalse":
              case "truefalse":
                return (
                  <TrueFalse
                    key={questionIdForComponent}
                    questions={content?.questions || [content]}
                    answers={testAnswers}
                    setAnswers={setTestAnswers}
                    mode="review"
                    questionId={questionIdForComponent}
                  />
                );
              case "mcq":
              case "quiz":
                return (
                  <MCQ
                    key={questionIdForComponent}
                    questions={content?.questions || [content]}
                    current={0}
                    answers={testAnswers}
                    setAnswers={setTestAnswers}
                    mode="review"
                    questionId={questionIdForComponent}
                  />
                );
              case "fillups":
                return (
                  <Fillups
                    key={questionIdForComponent}
                    questions={content?.questions || [content]}
                    current={0}
                    answers={testAnswers}
                    setAnswers={setTestAnswers}
                    mode="review"
                    questionId={questionIdForComponent}
                  />
                );
              default:
                return <div>Unsupported question type: {type}</div>;
            }
          })()}
        </div>
      </div>
    );
  };

  if (loading) {
    return <GlobalLoader />;
  }

  if (error) {
    return (
      <div
        className="test-page-container"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <h3>{error}</h3>
        <button className="btn-secondary" onClick={() => navigate(-1)}>
          Go back
        </button>
      </div>
    );
  }

  if (!showHomeworkAnswers) {
    return (
      <div className="test-page-container" style={{ display: "block" }}>
        <HomeworkFeedback
          homeworkMetadata={testMetadata}
          score={testScore}
          totalQuestions={questions.length}
          feedbackItems={homeworkFeedback}
          onShowAnswers={() => setShowHomeworkAnswers(true)}
          onBack={() => navigate("/homeworks")}
        />
      </div>
    );
  }

  return (
    <div className="test-page-container">
      <div className="test-header">
        <div className="test-info">
          <h2>{testMetadata?.name || "Homework Review"}</h2>
        </div>
        <div className="btns-wrapper">
          <div className="question-tracker">
            Question {currentQuestionIndex + 1} of {questions.length}
          </div>
          <button
            className="btn-secondary"
            onClick={() => setShowHomeworkAnswers(false)}
          >
            Back
          </button>
        </div>
      </div>

      <div className="test-body">
        {questions?.length > 0 ? (
          <div className="question-wrapper custom-scrollbar">
            {renderCurrentQuestion()}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <img
              src={notAvailable}
              alt="PowerPoint Icon"
              style={{ width: "300px", height: "300px", marginTop: "4px" }}
            />
            <h3>No Questions Found!</h3>
          </div>
        )}
        {questions?.length > 0 && (
          <QuestionPalette
            questions={questions}
            currentQuestionIndex={currentQuestionIndex}
            setCurrentQuestionIndex={setCurrentQuestionIndex}
            testAnswers={testAnswers}
            visitedQuestions={visitedQuestions}
          />
        )}
      </div>

      <div className="test-footer">
        <button
          className="btn-secondary"
          onClick={handlePrev}
          disabled={currentQuestionIndex === 0}
        >
          Previous
        </button>
        <div className="btns-wrapper">
          <button
            className={`btn-primary ${
              currentQuestionIndex === questions.length - 1 ? "disabled" : ""
            }`}
            onClick={handleNext}
            disabled={currentQuestionIndex === questions.length - 1}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomeworkReviewPage;
